import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectCurrentProjectInfo,
  selectProject,
  selectTimelineMetric,
  setTargetDate,
  setTimelineMetric,
} from 'state/workflowSlice';

import { ToggleGroup } from 'features/common/ToggleGroup';
import { OverflowLabel } from 'features/task_bar/OverflowLabel';
import {
  TL_METRICS_OPTIONS_NRT,
  TL_METRICS_OPTIONS_VNRT,
  TL_METRICS_OPTIONS_VNRT_EQUIVALENT,
  TL_METRIC_FF_REL_SPEED_VOL,
  TL_METRIC_FF_RT,
  TL_METRIC_FF_VNRT,
  TL_METRIC_TYP_REL_SPEED_VOL,
  TL_METRIC_TYP_REL_VOL,
  TL_METRIC_TYP_RT,
  TL_METRIC_TYP_VHD,
  TL_METRIC_TYP_VNRT,
  isRealtimeMetric,
} from 'features/workflow_timeline/metricsOptions';

import { useGetDatesQuery } from '../../state/apiSlice';
import { ComparisonPicker } from '../task_bar/ComparisonPicker';
import { findRTDate, useDayInfo } from '../task_bar/DatePicker';

import styles from '../task_bar/Taskbar.module.css';

const METRIC_FF = 'ff';
const METRIC_TYP = 'typ';
export const metricNames = {
  [TL_METRIC_TYP_REL_SPEED_VOL]: 'Atypical Speeds',
  [TL_METRIC_FF_REL_SPEED_VOL]: 'Speeds',
  [TL_METRIC_TYP_REL_VOL]: 'Atypical Volumes',
  [TL_METRIC_TYP_VHD]: 'Atypical Delay',
  [TL_METRIC_TYP_RT]: 'Atypical Realtime',
  [TL_METRIC_FF_RT]: 'Freeflow Realtime',
  [TL_METRIC_TYP_VNRT]: 'Atypical VNRT',
  [TL_METRIC_FF_VNRT]: 'Freeflow VNRT',
  [METRIC_TYP]: 'Atypical Slowdowns',
  [METRIC_FF]: 'Slowdowns',
};

export function TimelineMetricsOption() {
  const project = useAppSelector(selectProject);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const timelineMetric = useAppSelector(selectTimelineMetric);
  const dispatch = useAppDispatch();
  const [rtMetric, setRtMetric] = useState(METRIC_TYP);

  const { data: datesInfo } = useGetDatesQuery(project, { skip: !project });
  const { day_info } = useDayInfo();

  const [isRealtime, setIsRealtime] = useState(!day_info?.is_complete_vnrt);

  let availableMetrics;
  let safeDefault;
  let is_nrt = true;
  const has_rt_ever = datesInfo && findRTDate(datesInfo) !== undefined;

  if (!day_info || day_info.is_nrt) {
    availableMetrics = TL_METRICS_OPTIONS_NRT;
    // eslint-disable-next-line prefer-destructuring
    safeDefault = availableMetrics[0];
  } else {
    is_nrt = false;
    availableMetrics = TL_METRICS_OPTIONS_VNRT;
    safeDefault = day_info.is_complete_vnrt
      ? TL_METRIC_TYP_VNRT
      : TL_METRIC_TYP_RT;
  }

  const setMetric = (value) => {
    console.log(
      `TimelineMetricsOption handleChange newTimelineMetric: ${value}`,
    );
    if (day_info && isRealtimeMetric(value) && day_info.is_complete_vnrt) {
      // Are we on a day where there is no realtime?, but trying to set a realtime metric?
      const newDate = findRTDate(datesInfo);
      if (newDate) {
        dispatch(setTargetDate(newDate.date));
      } else {
        value = TL_METRICS_OPTIONS_VNRT_EQUIVALENT[value];
      }
    }
    dispatch(setTimelineMetric(value));
  };

  function rtMetricLookup(irtMetric: string, iisRealtime: boolean) {
    if (iisRealtime) {
      return { [METRIC_TYP]: TL_METRIC_TYP_RT, [METRIC_FF]: TL_METRIC_FF_RT }[
        irtMetric
      ];
    } else {
      return {
        [METRIC_TYP]: TL_METRIC_TYP_VNRT,
        [METRIC_FF]: TL_METRIC_FF_VNRT,
      }[irtMetric];
    }
  }

  function rtReverseLookup(iMetric: string) {
    return {
      [TL_METRIC_TYP_RT]: [METRIC_TYP, true],
      [TL_METRIC_FF_RT]: [METRIC_FF, true],
      [TL_METRIC_TYP_VNRT]: [METRIC_TYP, false],
      [TL_METRIC_FF_VNRT]: [METRIC_FF, false],
    }[iMetric];
  }

  const handleMetricToggleClick = (event) => {
    const newMetricToggleValue = event.target.value;
    setMetric(newMetricToggleValue);
  };

  const handleRtMetricToggleClick = (event) => {
    const newMetricToggleValue = event.target.value;
    setRtMetric(newMetricToggleValue);
    const newMetric = rtMetricLookup(newMetricToggleValue, isRealtime);
    setMetric(newMetric);
  };

  const handleRtNRTToggleClick = (event) => {
    const newIsRealtime = event.target.value === 'true';
    setIsRealtime(newIsRealtime);
    const newMetric = rtMetricLookup(rtMetric, newIsRealtime);
    setMetric(newMetric);
  };

  useEffect(() => {
    if (!availableMetrics.includes(timelineMetric) && day_info) {
      dispatch(setTimelineMetric(safeDefault));
    }
    if (!is_nrt && TL_METRICS_OPTIONS_VNRT.includes(timelineMetric)) {
      const [newRtMetric, newIsRealtime] = rtReverseLookup(timelineMetric);
      setRtMetric(newRtMetric as string);
      setIsRealtime(newIsRealtime as boolean);
    }
    if (is_nrt) {
      setIsRealtime(false);
    }
  }, [dispatch, timelineMetric, userProject, day_info, safeDefault]);

  let mainMetricSelector;
  if (is_nrt) {
    mainMetricSelector = (
      <div className={styles.metrics_space_above}>
        <OverflowLabel id="timeline-metric-picker-label">
          Metric:
          <ToggleGroup
            value={timelineMetric}
            values={availableMetrics}
            labelMap={metricNames}
            handleClick={handleMetricToggleClick}
            className={styles.toggle_group_metrics}
          />
        </OverflowLabel>
      </div>
    );
  } else {
    mainMetricSelector = (
      <div className={styles.metrics_space_above}>
        <OverflowLabel id="timeline-metric-picker-label">
          Metric:
          <ToggleGroup
            value={rtMetric}
            values={[METRIC_TYP, METRIC_FF]}
            labelMap={metricNames}
            handleClick={handleRtMetricToggleClick}
            className={styles.toggle_group_metrics}
          />
        </OverflowLabel>
      </div>
    );
  }

  const typicalMonthSelector = <ComparisonPicker />;
  if (day_info) {
    return (
      <div className={styles.task_bar_component}>
        {has_rt_ever && (
          <ToggleGroup
            handleClick={handleRtNRTToggleClick}
            value={String(isRealtime)}
            values={['false', 'true']}
            labelMap={{ true: 'Realtime', false: 'Recent' }}
            horizontal
          />
        )}
        {mainMetricSelector}
        {typicalMonthSelector}
      </div>
    );
  } else {
    return null;
  }
}
